import { FC, useContext, useEffect, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { useForm } from "react-hook-form";
import Contaier from "../../components/layout/Container/Container";
import Footer from "../../components/molecules/Footer/Footer";
import Header from "../../components/molecules/Header/Header";
import { AppContext, defaultAppContextData } from "../../services/appData";
import { useAuthService, useContextService } from "../../services";
import { IForeignUserModel } from "../../typings/models/ForeignUserModel";
import { UpdateForeignerUser } from "../../api/apimethods";
import { notify } from "../../utils";
import { NotifyType } from "../../utils/app-config";
import Loader from "../../components/molecules/Loader";

function MyProfile() {
  const { getContextValueFromStorage, putContextUser } = useContextService();
  const [contextData] = useState(() =>
    getContextValueFromStorage(defaultAppContextData)
  );
  const {
    register,
    setValue,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<IForeignUserModel>({ criteriaMode: "all" });
  const { message } = notify();
  const user = contextData.user;
  const address = user?.registeredAddresses?.[0];
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const getLoading = async () => {
      setIsEdit(false);
      setIsLoading(false);
    };

    if (isLoading) {
      getLoading();
      setValue("id", contextData?.user?.id);
      setValue("name", contextData?.user?.name);
      setValue("mobileNumber", contextData?.user?.mobileNumber);
      setValue("mobile", contextData?.user?.mobileNumber);
      setValue("email", contextData?.user?.email);
      setValue("emailAddress", contextData?.user?.email);
      setValue("foreignId", contextData?.user?.idNo);
      setValue("idNo", contextData?.user?.idNo);
      setValue("role", contextData?.user?.role);
      setValue("idType", contextData?.user?.idType);
      setValue("uen", contextData?.user?.uen);
      setValue("unitNo", address?.unit);
      setValue("floorNo", address?.floorLevel);
      setValue("blockNo", address?.blockHouseNo);
      setValue("buildingName", address?.buildingName);
      setValue("streetName", address?.streetName);
      setValue("postalCode", address?.postalCode);
    }
  }, [contextData?.user, isLoading]);

  const onClickEdit = () => {
    if (user?.userName != null) {
      setIsEdit(true);
    }
  };

  const onClickCancel = () => {
    setIsEdit(false);
  };

  const updateUser = async (userModel: IForeignUserModel) => {
    setLoading(true);
    userModel.emailAddress = contextData?.user?.email;
    userModel.id = contextData?.user?.id;
    try {
      const response = await UpdateForeignerUser(userModel);

      if (response && response?.data?.id) {
        message(NotifyType.Success, "Profile infomation updated successfully");
        setIsEdit(false);
        user.name = userModel?.name;
        user.mobileNumber = userModel?.mobile;
        const addressData = {
          blockHouseNo: userModel?.blockNo || "",
          floorLevel: userModel?.floorNo || "",
          unit: userModel?.unitNo || "",
          buildingName: userModel?.buildingName || "",
          streetName: userModel?.streetName || "",
          postalCode: userModel?.postalCode || "",
        };
        user.registeredAddresses = [addressData];
        putContextUser(user);
      } else {
        setIsEdit(true);
        message(
          NotifyType.Error,
          "failed to update Profile information.Please try again."
        );
      }
    } catch {
      setIsEdit(true);
      message(
        NotifyType.Error,
        "failed to update Profile information.Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onClickCreateUser = handleSubmit((userModel: IForeignUserModel) => {
    updateUser(userModel);
  });

  return (
    <>
      <Header />
      <Contaier>
        <div className="inner-content">
          <div className="container">
            <div className="personal-info profile-title">
              <h2>My Account</h2>
            </div>
            <div className="info-content">
              <div className="row">
                <div className="col-md-10"></div>
                {!isEdit && user?.role === "ForeignerLogin" && (
                  <div className="col-md-2">
                    <button
                      className="btn btn-link edit-icon float-end"
                      onClick={onClickEdit}
                    >
                      Edit
                    </button>
                  </div>
                )}
                {isEdit && user?.role === "ForeignerLogin" && (
                  <div className="col-md-1">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={onClickCreateUser}
                    >
                      {loading ? <Loader /> : <>Update</>}
                    </button>
                  </div>
                )}
                {isEdit && user?.role === "ForeignerLogin" && (
                  <div className="col-md-1">
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={onClickCancel}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="personal-info">
                  <h3 className="profile-subtitle">My Profile</h3>
                  <span className="form-check-label profile-desc">
                    Information here will be used to pre-fill your application
                    forms faster.
                  </span>
                </div>
                <div className="col-md-6">
                  {/* Name */}
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Name</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("name", {
                          required: "Name is required",
                          disabled: !isEdit,
                        })}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="name"
                        render={({ message }) => (
                          <p className="err-msg star-red">{message}</p>
                        )}
                      />
                    </div>
                  </div>
                  {/* Email */}
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Email</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("emailAddress", {
                          required: "Email is required",
                          disabled: true,
                        })}
                      />
                    </div>
                  </div>
                  {/* ID Type */}
                  {user?.role === "ForeignerLogin" && (
                    <div className="row mb-2">
                      <div className="col-md-4">
                        <h6>ID Type</h6>
                      </div>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          {...register("idType", {
                            required: "Dd Type is required",
                            disabled: true,
                          })}
                        />
                      </div>
                    </div>
                  )}
                  {/* ID Number */}
                  {user?.role === "ForeignerLogin" && (
                    <div className="row  mb-2">
                      <div className="col-md-4">
                        <h6>Foreign ID Number</h6>
                      </div>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          {...register("foreignId", {
                            required: "Id No is required",
                            disabled: true,
                          })}
                        />
                      </div>
                    </div>
                  )}
                  {/* Uen */}
                  {user.role === "CorpPass" && (
                    <div className="row mb-2">
                      <div className="col-md-4">
                        <h6>UEN</h6>
                      </div>
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          {...register("uen", {
                            required: "Uen is required",
                            disabled: true,
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  {/* Login Type */}
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Login Type</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("role", {
                          required: "Role is required",
                          disabled: true,
                        })}
                      />
                    </div>
                  </div>
                  {/* Contact No */}
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Contact No</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("mobile", {
                          required: "Contact no is required",
                          disabled: !isEdit,
                        })}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="mobile"
                        render={({ message }) => (
                          <p className="err-msg star-red">{message}</p>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="info-content">
              <div className="row">
                <div className="personal-info">
                  <h3 className="profile-subtitle">Address</h3>
                  <span className="form-check-label profile-desc">
                    This address will be used in all your application forms.
                  </span>
                </div>
                <div className="col-md-6">
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Block No</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("blockNo", {
                          required: "Block No is required",
                          disabled: !isEdit,
                        })}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Unit</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("unitNo", {
                          required: "Unit No is required",
                          disabled: !isEdit,
                        })}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Street</h6>
                    </div>
                    <div className="col-md-8">
                      <textarea
                        className="form-control"
                        rows={3}
                        cols={2}
                        {...register("streetName", {
                          required: "Street is required",
                          disabled: !isEdit,
                        })}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="streetName"
                        render={({ message }) => (
                          <p className="err-msg star-red">{message}</p>
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Building Name</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("buildingName", {
                          required: "Building name is required",
                          disabled: !isEdit,
                        })}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Floor</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("floorNo", {
                          required: "Floor No is required",
                          disabled: !isEdit,
                        })}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <h6>Postal Code</h6>
                    </div>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        {...register("postalCode", {
                          required: "Postal Code is required",
                          disabled: !isEdit,
                        })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Contaier>

      {(user.role === "CorpPass" || user.role === "SingPass") && (
        <div className="info-content d-flex justify-content-center align-items-center text-center">
          <p>
            {user.role === "CorpPass"
              ? "For changes to Corppass information, please contact your Corppass administrator."
              : "For changes to Singpass information, please update via Singpass application."}
          </p>
        </div>
      )}

      <Footer />
    </>
  );
}

export default MyProfile;
